import { useContext } from 'react';
import { format } from 'date-fns';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useAppEvent from 'hooks/useAppEvent';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import {
  AppEventType,
  PropertyPricingPeriodsUpdatedEventPayload,
} from 'models/Events';
import useCalendarBodySelection from 'pages/calendar/common/body/selection/useCalendarBodySelection';
import { extractErrorMessage } from 'utils/axiosExceptionUtils';
import { normalizeKey } from 'utils/localeUtils';
import { PropertyPricingModalSchemaType } from './PropertyPricingModal.schema';
import PropertyPricingModalContext from './PropertyPricingModalContext';
import useResetPropertiesPricingMutation from './useResetPropertiesPricingMutation';

const useResetPropertiesPricing = () => {
  const { t } = useTranslation();
  const { resetSelection } = useCalendarBodySelection();
  const { getValues } = useFormContext<PropertyPricingModalSchemaType>();
  const { modalId, params } = useContext(PropertyPricingModalContext);
  const { publish } = useAppEvent();
  const { closeModal } = useAppModal();
  const { notifyError } = useNotify();
  const { mutateAsync, isLoading: isResetting } =
    useResetPropertiesPricingMutation({
      onSuccess: (data, payload) => {
        const { startDate, endDate, propertyUids } = payload;
        const updatedPricingPeriods: PropertyPricingPeriodsUpdatedEventPayload =
          {
            updatedPricingPeriods: propertyUids?.flatMap((propertyUid) => [
              { date: startDate, propertyUid },
              { date: endDate, propertyUid },
            ]),
          };

        publish(
          AppEventType.PROPERTY_PRICING_PERIODS_UPDATED,
          updatedPricingPeriods,
        );

        closeModal(modalId);
        resetSelection();
      },
      onError: (err) => {
        const errorMessage = extractErrorMessage(err);
        const messageToDisplay = t(normalizeKey(errorMessage));
        notifyError(messageToDisplay);
      },
    });

  const resetPropertiesPricing = async () => {
    const { startDate, endDate } = getValues();

    await mutateAsync({
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      propertyUids: params.propertyUids,
    });
  };

  return { isResetting, resetPropertiesPricing };
};

export default useResetPropertiesPricing;
